import * as THREE from "three";
import TWEEN from "@tweenjs/tween.js";
import { EffectComposer, ShaderPass } from "three-stdlib";

/**
 * Manages what is displayed on the screen and the transitions.
 */
export default class SceneManager {
  currentFov;

  /**
   * Creates all the necessary objects
   */
  constructor(renderer, camera, player) {
    // This scene is used when a transition is in progress
    this._scene = new THREE.Scene();
    this._renderer = renderer;
    this._camera = camera;
    this._player = player;

    this._cameraOrtho = new THREE.OrthographicCamera(
      window.innerWidth / -2,
      window.innerWidth / 2,
      window.innerHeight / 2,
      window.innerHeight / -2,
      -10,
      10,
    );

    // The composer is used to define the render passes
    this._composer = new EffectComposer(renderer);

    // Create distortion parallax pass
    this._parallaxPass = new ShaderPass(this.createParallaxShader());
    this._composer.addPass(this._parallaxPass);

    // Create fading pass
    // Fades between scenes
    this._fadePass = new ShaderPass(this.createFadeShader());
    this._composer.addPass(this._fadePass);

    // Create blur passes
    this._blurPasses = this.createBlurPasses();
    for (let b in this._blurPasses) {
      // this._composer.addPass(this._blurPasses[b]);

      if (b === this._blurPasses.length - 1) {
        this._blurPasses[b].renderToScreen = true;
      }
    }

    // Keeps a list of all active scenes
    this._scenes = [];

    // Params
    this._transitionSpeed = 750;
    this._transition = 0.0;

    this._transitioning = false;
    this._player.setIsTransitioning(false);
  }

  /**
   * Runs callback on every hotspot image found in the scene
   *
   * @param {THREE.Scene} scene
   * @param {Object} config
   * @param {function} callback
   */
  forEachHotspotImageInScene(scene, config, callback = () => { }, max = 4) {
    if ("_image" in scene && "hotspots" in scene._image) {
      const hotspots = this._player.getHotspotsFromImage(scene._image.id);
      hotspots.slice(0, max).forEach(hotspot => {
        if (hotspot && "type" in hotspot && hotspot.type === "link") {
          if ("options" in hotspot && "id" in hotspot.options) {
            const imageID = hotspot.options.id;

            // Check if properties exist and get image
            if ("images" in config) {
              const image = config.images[imageID];
              callback(image);
            }
          }
        }
      });
    }
  }

  /**
   * Add as a scene to the list
   *
   * @param {THREE.Scene} scene
   */
  addScene(scene) {
    // Push the current scene
    this._scenes.push(scene);
  }

  switchScene(scene) {
    this._scenes = [scene];
  }

  /**
   * Returns the active scene
   *
   * @return {Scene360 || Scene3D || SceneSplit} scene
   */
  activeScene() {
    return this._scenes[0];
  }

  containsHolder(_holder) {
    return this._scenes[0]._photoHolder === _holder;
  }

  dispose() {
    //Dispose of shaders
    this._parallaxPass.material.dispose();
    this._fadePass.material.dispose();

    this._parallaxPass.material = undefined;
    this._fadePass.material = undefined;

    this._parallaxPass.quad = undefined;
    this._fadePass.quad = undefined;

    for (var i = 0; i < this._scenes.length; i++) {
      this._scenes[i].dispose();
      this._scenes[i].destroy();
    }
  }

  /**
   * Transition by fading one scene into the other (no movement)
   */
  startFadeTransition(transitionSpeed = 1) {
    if (this._scenes.length > 1) {
      this._parallaxPass.uniforms.tDiffuse1.value = this._scenes[0].fbo;
      this._fadePass.uniforms.tDiffuse1.value = this._scenes[0].fbo;
      this._fadePass.uniforms.tDiffuse2.value = this._scenes[1].fbo;
      this._transitioning = true;
      this._player.setIsTransitioning(true);
      this._transition = 0;

      // face out hotspots

      const hotspots = this._scenes[0]._hotspots;
      const newHotspots = this._scenes[1]._hotspots;

      if (newHotspots !== undefined) {
        for (const hotspot of newHotspots) {
          hotspot.setOpacity(0);
        }
      }

      const opacity = { value: 1 };
      const opacityTween = new TWEEN.Tween(opacity).to(
        { value: 0 },
        this._transitionSpeed * 0.1 * transitionSpeed,
      );
      opacityTween.onUpdate(() => {
        for (const hotspot of hotspots) {
          hotspot.setOpacity(opacity.value);
        }
      });

      // fade new hotspots in
      const newOpacityTween = new TWEEN.Tween(opacity).to(
        { value: 1 },
        this._transitionSpeed * 0.2 * transitionSpeed,
      );
      newOpacityTween.onUpdate(() => {
        if (newHotspots === undefined) {
          return;
        }

        for (const hotspot of newHotspots) {
          hotspot.setOpacity(opacity.value);
        }
      });

      const tween = new TWEEN.Tween(this).to(
        { _transition: 1 },
        this._transitionSpeed * 0.5 * transitionSpeed,
      );
      tween.easing(TWEEN.Easing.Cubic.In);
      tween.chain(newOpacityTween);
      // tween.delay(this._transitionSpeed * 0.2);

      const n = { n: 0 };
      this._parallaxPass.uniforms.n.value = n.n;

      const blurAmount = { n: 0 };
      for (let s = 0; s < this._blurPasses.length; s += 1) {
        if (s % 2 === 0) {
          this._blurPasses[s].uniforms.h.value =
            1.1 / ((1.2 - blurAmount.n) * (window.innerHeight * 4));
        } else {
          this._blurPasses[s].uniforms.v.value =
            1.1 / ((1.2 - blurAmount.n) * (window.innerWidth * 4));
        }
      }

      tween.onComplete(() => {
        //console.log(this._scenes[0].loadFullRes);
        if ("loadFullRes" in this._scenes[1]) {
          this._scenes[1].loadFullRes();
        }
      });

      // fov
      let target_fov = this._scenes[1].getTargetFov();
      let fovTween = this.tweenFov(
        target_fov,
        null,
        this._transitionSpeed * 0.8 * transitionSpeed,
      );

      if (this._scenes[0]._photoHolder.beforeTransition) {
        this._scenes[0]._photoHolder.beforeTransition();
      }

      // Start the transition on the player side
      setTimeout(() => {
        tween
          .onComplete(() => {
            if (this._scenes[1]._photoHolder.afterTransition) {
              this._scenes[1]._photoHolder.afterTransition();
            }
          })
          .start();
        if (hotspots !== undefined) {
          opacityTween.start();
        }
        if (fovTween) {
          fovTween.start();
        }
      });
    } else if (this._scenes.length === 1) {
      if (this._scenes[0].loadFullRes) {
        this._scenes[0].loadFullRes();
      }
    }
  }

  /**
   * Start transition
   *
   */
  startTransition(triggeredAt, targetPosition, parallax = true) {
    if (this._scenes.length > 1) {
      // Scene to transition from - this._scenes[0]
      // Scene to trantition to - this._scenes[1]
      this._parallaxPass.uniforms.tDiffuse1.value = this._scenes[0].fbo;
      this._fadePass.uniforms.tDiffuse1.value = this._scenes[0].fbo;
      this._fadePass.uniforms.tDiffuse2.value = this._scenes[1].fbo;

      this._transitioning = true;
      this._player.setIsTransitioning(true);
      this._transition = 0;

      // fade new hotspots in

      const newHotspots = this._scenes[1]._hotspots;

      if (newHotspots !== undefined) {
        for (const hotspot of newHotspots) {
          hotspot.setOpacity(0);
        }
      }

      const opacity = { value: 1 };
      const newOpacityTween = new TWEEN.Tween(opacity).to(
        {
          value: 1,
        },
        this._transitionSpeed * 0.2,
      );
      newOpacityTween.onUpdate(() => {
        if (newHotspots === undefined) {
          return;
        }

        for (const hotspot of newHotspots) {
          hotspot.setOpacity(opacity.value);
        }
      });

      const tween = new TWEEN.Tween(this).to(
        {
          _transition: 1,
        },
        this._transitionSpeed * 0.5,
      );
      tween.easing(TWEEN.Easing.Cubic.In);
      tween.chain(newOpacityTween);
      tween.delay(this._transitionSpeed * 0.2);

      // fov

      let target_fov = this._scenes[1].getTargetFov();
      let fovTween = this.tweenFov(
        target_fov,
        null,
        this._transitionSpeed * 0.8,
      );

      // Start the transition on the scene
      const startSceneTween = this._scenes[0].startTransition(
        targetPosition,
        this._transitionSpeed,
        undefined,
        "In",
        triggeredAt,
        true,
      );
      var newTriggeredAt, tmpTriggeredAt;

      if (triggeredAt) {
        newTriggeredAt = {
          x: triggeredAt.x,
          y: triggeredAt.y,
          z: triggeredAt.z,
        };

        tmpTriggeredAt = {
          x: triggeredAt.x,
          y: triggeredAt.y,
          z: triggeredAt.z,
        };
      }

      if (this._scenes[0]._startingRotation) {
        let rotation = this._scenes[0]._startingRotation;
        //rotation.y += this._player.controlsOrbit.getAzimuthalAngle()
        tmpTriggeredAt = this.getNewTriggeredAt(rotation, triggeredAt, 0);
      }

      let endSceneTween = false;
      if (this._scenes[1]._startingRotation) {
        let rotation = this._scenes[1]._startingRotation;
        //rotation.y = this._player.controlsOrbit.getAzimuthalAngle();
        newTriggeredAt = this.getNewTriggeredAt(rotation, tmpTriggeredAt, 1);
        endSceneTween = this._scenes[1].startTransition(
          {
            x: 0,
            y: 0,
            z: 0,
          },
          this._transitionSpeed,
          newTriggeredAt,
          "Out",
        );
      } else {
        endSceneTween = this._scenes[1].startTransition(
          {
            x: 0,
            y: 0,
            z: 0,
          },
          this._transitionSpeed,
          tmpTriggeredAt,
          "Out",
        );
      }

      let prev = this._scenes[0]._photoHolder;
      let subject = this._scenes[1]._photoHolder;
      let subjectScene = this._scenes[1];

      if (prev.beforeTransition) {
        prev.beforeTransition();
      }

      startSceneTween.start();
      endSceneTween
        .onComplete(() => {
          if (subject) {
            if (subject.afterTransition) {
              subject.afterTransition();
              if ("loadFullRes" in subjectScene) {
                subjectScene.loadFullRes();
              }
            }
          }
        })
        .start();
      tween.start();
      if (fovTween) {
        fovTween.start();
      }
    } else if (this._scenes.length === 1) {
      if (this._scenes[0].loadFullRes) {
        this._scenes[0].loadFullRes();
      }
    }
  }

  tweenFov(newFov, ease = null, speed = null) {
    speed = speed ?? this._transitionSpeed; // * 0.5;
    ease = ease ?? TWEEN.Easing.Quadratic.In;

    if (newFov === this._player.camera.fov) {
      return false;
    }

    this.currentFov = { fov: this._player.camera.fov };

    return new TWEEN.Tween(this.currentFov)
      .to({ fov: newFov }, speed)
      .easing(ease)
      .onUpdate(() => {
        this._player.camera.fov = this.currentFov.fov;
        this._player.camera.updateProjectionMatrix();
      });
  }

  getNewTriggeredAt(startingRotation, oldPosition, order) {
    // var gammar = startingRotation.x;
    // var beta = startingRotation.y;
    // var alpha = startingRotation.z;
    var cosA = Math.cos(startingRotation.z);
    var sinA = Math.sin(startingRotation.z);
    var cosB = Math.cos(startingRotation.y);
    var sinB = Math.sin(startingRotation.y);
    var cosG = Math.cos(startingRotation.x);
    var sinG = Math.sin(startingRotation.x);

    if (order === 0) {
      cosA = Math.cos(-startingRotation.z);
      sinA = Math.sin(-startingRotation.z);
      cosB = Math.cos(-startingRotation.y);
      sinB = Math.sin(-startingRotation.y);
      cosG = Math.cos(-startingRotation.x);
      sinG = Math.sin(-startingRotation.x);
    }

    const x =
      cosA * cosB * oldPosition.x +
      (cosA * sinB * sinG - sinA * cosG) * oldPosition.y +
      (cosA * sinB * cosG + sinA * sinG) * oldPosition.z;
    const y =
      sinA * cosB * oldPosition.x +
      (sinA * sinB * sinG + cosA * cosG) * oldPosition.y +
      (sinA * sinB * cosG - cosA * sinG) * oldPosition.z;
    const z =
      -sinB * oldPosition.x +
      cosB * sinG * oldPosition.y +
      cosB * cosG * oldPosition.z;

    return { x, y, z };
  }

  /**
   * Render the scene.
   * If no transition is happening, then just render the first scene.
   *
   * @param {THREE.Renderer} renderer
   * @param {THREE.Camera} camera
   */
  render(renderer, camera) {
    TWEEN.update();

    if (!this._transitioning) {
      this._scenes[0].render(renderer, camera);
      return;
    }

    if (this._player.viewerMode === "stereo" || renderer.xr.enabled) {
      this._scenes[0].render(renderer, camera);
      if (this._transition === 1) {
        this._transition = 0;
        this._transitioning = false;
        this._player.setIsTransitioning(false);
        // should we dispose first?
        this._scenes[0].destroy();
        this._scenes.shift();
      }
      return;
    }

    this._fadePass.uniforms.mixRatio.value = this._transition;

    this._scenes[0].render(renderer, camera, true);
    this._scenes[1].render(renderer, camera, true);

    this._composer.render();

    if (this._transition === 1) {
      this._transition = 0;
      this._transitioning = false;
      this._player.setIsTransitioning(false);
      // should we dispose first?
      this._scenes[0].destroy();
      this._scenes.shift();
    }
  }

  isTransitioning() {
    return this._transitioning;
  }

  createBlurPasses() {
    // Blur amount by default is large
    const firstShaders = this.createBlurShader(
      window.innerWidth * 3,
      window.innerHeight * 3,
    );

    const passes = [];
    passes.push(new ShaderPass(firstShaders[0]));
    passes.push(new ShaderPass(firstShaders[1]));

    return passes;
  }

  createBlurShader(h, v) {
    const vertexShader = `
      varying vec2 vUv;

      void main() {
        vUv = vec2( uv.x, uv.y );
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
      }
    `;

    const hBlur = {
      uniforms: {
        tDiffuse: { type: "t", value: null },
        h: { type: "f", value: 1.0 / h },
      },
      vertexShader,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float h;

        varying vec2 vUv;

        void main() {
          vec4 sum = vec4( 0.0 );

          vec4 originalSample = texture2D( tDiffuse, vUv );

          sum += texture2D( tDiffuse, vec2( vUv.x - 4.0 * h, vUv.y ) ) * 0.0162;
          sum += texture2D( tDiffuse, vec2( vUv.x - 3.0 * h, vUv.y ) ) * 0.0540;
          sum += texture2D( tDiffuse, vec2( vUv.x - 2.0 * h, vUv.y ) ) * 0.1216;
          sum += texture2D( tDiffuse, vec2( vUv.x - 1.0 * h, vUv.y ) ) * 0.1945;
          sum += originalSample * 0.2270;
          sum += texture2D( tDiffuse, vec2( vUv.x + 1.0 * h, vUv.y ) ) * 0.1945;
          sum += texture2D( tDiffuse, vec2( vUv.x + 2.0 * h, vUv.y ) ) * 0.1216;
          sum += texture2D( tDiffuse, vec2( vUv.x + 3.0 * h, vUv.y ) ) * 0.0540;
          sum += texture2D( tDiffuse, vec2( vUv.x + 4.0 * h, vUv.y ) ) * 0.0162;

          gl_FragColor = sum;
        }
      `,
    };

    const vBlur = {
      uniforms: {
        tDiffuse: { type: "t", value: null },
        v: { type: "f", value: 1.0 / v },
      },
      vertexShader,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float v;

        varying vec2 vUv;

        void main() {
          vec4 sum = vec4( 0.0 );

          vec4 originalSample = texture2D( tDiffuse, vUv );

          sum += texture2D( tDiffuse, vec2( vUv.x, vUv.y - 4.0 * v ) ) * 0.0162;
          sum += texture2D( tDiffuse, vec2( vUv.x, vUv.y - 3.0 * v ) ) * 0.0540;
          sum += texture2D( tDiffuse, vec2( vUv.x, vUv.y - 2.0 * v ) ) * 0.1216;
          sum += texture2D( tDiffuse, vec2( vUv.x, vUv.y - 1.0 * v ) ) * 0.1945;
          sum += originalSample * 0.2270;
          sum += texture2D( tDiffuse, vec2( vUv.x, vUv.y + 1.0 * v ) ) * 0.1945;
          sum += texture2D( tDiffuse, vec2( vUv.x, vUv.y + 2.0 * v ) ) * 0.1216;
          sum += texture2D( tDiffuse, vec2( vUv.x, vUv.y + 3.0 * v ) ) * 0.0540;
          sum += texture2D( tDiffuse, vec2( vUv.x, vUv.y + 4.0 * v ) ) * 0.0162;

          gl_FragColor = sum;
        }
      `,
    };

    return [hBlur, vBlur];
  }

  createParallaxShader() {
    const shader = {
      uniforms: {
        tDiffuse1: { type: "t", value: null },
        n: { type: "f", value: 0.0 },
      },
      vertexShader: `
        varying vec2 vUv;

        void main() {
          vUv = vec2( uv.x, uv.y );
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse1;
        varying vec2 vUv;
        uniform float n;

        void main() {
          vec4 tex = texture2D( tDiffuse1, vUv );
          float h = (((tex.x + tex.y + tex.z) / 3.0) / 20.0) * n;
          gl_FragColor = texture2D( tDiffuse1, vec2( vUv.x + (h * -(vUv.x * 2.0 - 1.0)), vUv.y + (h * -(vUv.y * 2.0 - 1.0))) );
          // 'gl_FragColor = texture2D( tDiffuse1, vUv);',
        }
      `,
    };

    return shader;
  }

  createFadeShader() {
    const shader = {
      uniforms: {
        tDiffuse1: { type: "t", value: null },
        tDiffuse2: { type: "t", value: null },
        mixRatio: { type: "f", value: 0.0 },
      },
      vertexShader: `
        varying vec2 vUv;

        void main() {
          vUv = vec2( uv.x, uv.y );
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
      `,
      fragmentShader: `
        uniform float mixRatio;
        uniform sampler2D tDiffuse1;
        uniform sampler2D tDiffuse2;

        varying vec2 vUv;

        void main() {
          vec4 texel1 = texture2D( tDiffuse1, vUv );
          vec4 texel2 = texture2D( tDiffuse2, vUv );
          gl_FragColor = mix( texel1, texel2, mixRatio );
        }
      `,
    };

    return shader;
  }
}
